import { DataTableDefinition } from "@/v2/types/Datatable";
import { createColumnHelper, flexRender } from "@tanstack/react-table";
import { buildColumns } from "./buildColumns";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Eye, Pencil } from "lucide-react";
import { Supplier } from "@/v2/types";
import { generateFilterColumn } from "../../filter";
import AccessControl from "../../rbac/AccessControl";
import SupplierDeleteButton from "../../ui/button/delete/Supplier";

type T = Supplier;

const columnHelper = createColumnHelper<T>();

const SuppliersColumnDefinition: DataTableDefinition<T>[] = [
    {
        id: "sl",
        headingText: "Sl No",
        headingCellType: "small-cell",
        cellType: "text-center",
    },
    {
        id: "full_name",
        headingText: "Supplier Name",
        headingCellType: "text-center",
    },
    {
        id: "short_code",
        headingText: "Short Code",
        headingCellType: "text-center",
        cellType: "text-center",
    },
    {
        id: "email",
        headingText: "Email",
        headingCellType: "text-center",
        cellType: "text-center",
    },
    {
        id: "phone_number",
        headingText: "Phone Number",
        headingCellType: "text-center",
        cellType: "text-center",
    },
    {
        id: "representatives.full_name",
        headingText: "( name )",
        headingCellType: "representative",
        cell: ({ row }) => {
            return (
                <div className="text-left">
                    {row.representatives?.at(0)?.full_name}
                </div>
            );
        },
    },
    {
        id: "representatives.phone_number",
        headingText: "( number )",
        headingCellType: "representative",
        cell: ({ row }) => {
            return (
                <div className="text-left">
                    {row.representatives?.at(0)?.phone_number}
                </div>
            );
        },
    },
    {
        id: "representatives.email",
        headingText: "( email )",
        headingCellType: "representative",
        cell: ({ row }) => {
            return (
                <div className="text-left">
                    {row.representatives?.at(0)?.email}
                </div>
            );
        },
    },
    {
        id: "locations",
        headingText: "Location",
        headingCellType: "text-center",
        cell: ({ row }) => {
            return (
                <div className="min-w-[300px]">
                    {row.locations?.map((location) => (
                        <p key={location}>{location}</p>
                    ))}
                </div>
            );
        },
    },

    {
        id: "assigned_supplier",
        headingText: "Coordinator",
        headingCellType: "text-center",
        cell: ({ row }) => {
            return (
                <div className="text-center">
                    {row.assigned_supplier?.map((assignedSupplier) => (
                        <p key={assignedSupplier.id}>
                            {assignedSupplier.full_name}
                        </p>
                    ))}
                </div>
            );
        },
    },

    {
        id: "actions",
        headingText: "Actions",
        headingCellType: "text-center",
        cell: ({ row }) =>
            flexRender(
                <div className="flex justify-center gap-2">
                    <Link to={`/supplier-database/${row.id}`}>
                        <Button size={"sm"} variant={"outline"}>
                            <Eye className="size-4" />
                        </Button>
                    </Link>
                    <AccessControl
                        module="SUPPLIER_MANAGEMENT"
                        requestedAccess={"PUT"}
                    >
                        <Link to={`/supplier-database/edit/${row.id}`}>
                            <Button size={"sm"} variant={"outline"}>
                                <Pencil className="size-4" />
                            </Button>
                        </Link>
                    </AccessControl>

                    <AccessControl
                        module="SUPPLIER_MANAGEMENT"
                        requestedAccess={["DELETE"]}
                    >
                        <SupplierDeleteButton id={row.id} />
                    </AccessControl>
                </div>,
                {},
            ),
    },
];

const SuppliersColumn = buildColumns<T>(
    columnHelper,
    SuppliersColumnDefinition,
);

const SuppliersFilterColumn = generateFilterColumn<T>(
    SuppliersColumnDefinition,
    {
        removeIds: ["full_name_ar"],
        renameMap: {
            "representatives.full_name": "Representative Name",
            "representatives.email": "Representative Email",
            "representatives.phone_number": "Representative Phone Number",
        },
        addFilterId: {
            assigned_supplier: "assigned_suppliers.full_name",
        },
    },
);

export { SuppliersColumn, SuppliersFilterColumn };
